import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Star } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { contentApi } from '../../services/contentApi'
import { getImageUrl, IMAGE_CONFIG } from '../../config/settings'

const VendorDetailPage = () => {
  const { username } = useParams<{ username: string }>()
  const [vendor, setVendor] = useState<Awaited<ReturnType<typeof contentApi.getVendor>> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!username) return
    contentApi.getVendor(username)
      .then(setVendor)
      .catch(() => setVendor(null))
      .finally(() => setLoading(false))
  }, [username])

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>
  if (!vendor) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold">Vendor not found</h1><Link to="/vendors" className="mt-4 inline-block text-primary-600">← All vendors</Link></div>

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero title={vendor.name} subtitle={vendor.address || `${vendor.city}, ${vendor.state}`}>
        <div className="flex items-center gap-4">
          <img src={vendor.photo ? getImageUrl(vendor.photo) : IMAGE_CONFIG.THUMBNAIL_FALLBACK} alt={vendor.name} className="h-20 w-20 rounded-2xl object-cover ring-4 ring-white/20" />
          <div>
            <p className="text-white/70">@{vendor.username}</p>
            {vendor.avg_rating > 0 && (
              <p className="mt-1 inline-flex items-center gap-1 font-semibold"><Star className="h-4 w-4 fill-primary-400 text-primary-400" /> {vendor.avg_rating.toFixed(1)} rating</p>
            )}
          </div>
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/vendors" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 mb-8"><ArrowLeft className="h-4 w-4" /> All vendors</Link>

        {vendor.details && (
          <div className="mb-10 p-6 rounded-2xl bg-white border border-gray-200 prose max-w-none" dangerouslySetInnerHTML={{ __html: vendor.details }} />
        )}

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Rooms by {vendor.name}</h2>
        {vendor.rooms?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendor.rooms.map((room) => (
              <Link key={room.id} to={`/room/${room.id}`} className="group rounded-2xl overflow-hidden bg-white border border-gray-200 hover:shadow-lg transition-all">
                <img src={room.featured_img ? getImageUrl(room.featured_img) : IMAGE_CONFIG.FALLBACK_IMAGE} alt={room.title} className="aspect-[4/3] w-full object-cover group-hover:scale-105 transition-transform" />
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900">{room.title}</h3>
                  <p className="mt-1 text-primary-700 font-bold">₹{room.rent.toLocaleString('en-IN')} <span className="text-sm font-normal text-gray-500">/ night</span></p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No active rooms from this vendor right now.</p>
        )}
      </section>
    </div>
  )
}

export default VendorDetailPage
